import * as THREE from 'three';

/**
 * Per-figure shader data carried in the figure's bone texture, after its bone matrices (the leg capsules cloth is pushed from, the cloth
 * cage's offsets). Three binds a skeleton's bone texture with every draw, while it re-sends all of a material's uniforms whenever
 * consecutive draws switch material; data kept here instead of in uniforms leaves a figure's materials free to be shared with every
 * other figure (`figure-materials.ts`), so a crowd's draws stop re-sending them.
 *
 * Texel offsets are fixed when reserved and compiled into the shaders that read them; figures with the same skeleton reserve the same
 * offsets, so their shaders match.
 */
interface Extra { texels: number; after: (() => void)[] }
const extras = new WeakMap<THREE.Skeleton, Extra>();

function extra(skeleton: THREE.Skeleton): Extra {
  let entry = extras.get(skeleton);
  if (entry) return entry;
  entry = { texels: 0, after: [] };
  extras.set(skeleton, entry);
  const own = entry, update = skeleton.update;
  skeleton.computeBoneTexture = function (this: THREE.Skeleton) {
    // Three's layout: a square, a multiple of four texels wide, so each bone's four texels share a row.
    const size = Math.max(4, Math.ceil(Math.sqrt(this.bones.length * 4 + own.texels) / 4) * 4);
    const matrices = new Float32Array(size * size * 4);
    if (this.boneMatrices) matrices.set(this.boneMatrices.subarray(0, Math.min(this.boneMatrices.length, matrices.length)));
    this.boneTexture?.dispose();
    this.boneMatrices = matrices;
    this.boneTexture = new THREE.DataTexture(matrices, size, size, THREE.RGBAFormat, THREE.FloatType);
    this.boneTexture.needsUpdate = true;
    return this;
  };
  // Three updates a skeleton once a frame, after world matrices and before any of its surfaces draw.
  skeleton.update = function (this: THREE.Skeleton) {
    update.call(this);
    for (const after of own.after) after();
  };
  return entry;
}

/** Reserves `count` texels for a figure's own data; returns the first one's index, counted in texels from the texture's start. */
export function reserveTexels(skeleton: THREE.Skeleton, count: number) {
  const entry = extra(skeleton), offset = skeleton.bones.length * 4 + entry.texels;
  entry.texels += count;
  skeleton.computeBoneTexture();
  return offset;
}

/** The texture's data (four floats a texel), where a figure writes its reserved texels. Mark it changed with `touchTexels`. */
export function figureTexels(skeleton: THREE.Skeleton) { return skeleton.boneMatrices!; }
export function touchTexels(skeleton: THREE.Skeleton) { if (skeleton.boneTexture) skeleton.boneTexture.needsUpdate = true; }

/** Runs `after` each time three updates the skeleton for a frame (bone world matrices are then current). */
export function afterSkeletonUpdate(skeleton: THREE.Skeleton, after: () => void) { extra(skeleton).after.push(after); }

/** Declares `vec4 figureTexel(int texel)` in a skinned vertex shader, once however many hooks ask. */
export function withFigureTexels(vertexShader: string) {
  if (vertexShader.includes('vec4 figureTexel(')) return vertexShader;
  return vertexShader.replace('#include <skinning_pars_vertex>', `#include <skinning_pars_vertex>
    #ifdef USE_SKINNING
    vec4 figureTexel(int texel) { int size = textureSize(boneTexture, 0).x; return texelFetch(boneTexture, ivec2(texel % size, texel / size), 0); }
    #endif`);
}
